import {Component, Inject, OnInit} from '@angular/core';
import {MAT_DIALOG_DATA, MatDialogRef} from '@angular/material/dialog';
import {FormGroup, FormControl, Validators, ValidatorFn, AbstractControl} from '@angular/forms';
import {ItemDataService} from '@emm/common-items/services/item-data.service';
import {NotificationService} from '@emm/shared/services/notification.service';
import {API_ERRORS} from '@emm/core/util/api-constants';
import {take, startWith, map as rxMap} from 'rxjs/operators';
import {CreateVariation, Variation} from '@emm/common-items/models/variation.model';
import {COMMA, ENTER} from '@angular/cdk/keycodes';
import {LocationSetService} from '@emm/core/services/location-set.service';
import {LocationSet} from '@emm/core/models/location-set.model';
import {forkJoin, Observable} from 'rxjs';
import {formatFieldName} from '@emm/core/util/string-format.util';
@Component({
    selector: 'emm-create-variation-dialog',
    templateUrl: './create-variation-dialog.component.html',
    styleUrl: './create-variation-dialog.component.scss'
})
export class CreateVariationDialogComponent implements OnInit {

    effectiveDate: Date | null = new Date(Date.now() + 24 * 60 * 60 * 1000);
    templateForm = new FormGroup ({
        template: new FormControl(1),
        effectiveDate: new FormControl(this.effectiveDate),
        location: new FormControl('', Validators.required),
    });
    form: FormGroup;
    variationTemplates = [];
    groupedTemplates: { scope: string, templates: any[] }[] = [];
    formReady = false;
    templateAttributes;
    separatorKeysCodes: number[] = [ENTER, COMMA];

    locationSets: LocationSet[] = [];
    filteredLocationSets: Observable<LocationSet[]>;
    selectedLocationSet: LocationSet | null = null;
    selectedTabIndex = 0;
    existingVariations: Variation[] = [];
    metadataAttributes: any[] = [];
    overrideAttributes: any[] = [];


    constructor(
        private itemDataService: ItemDataService,
        private notificationService: NotificationService,
        private locationSetService: LocationSetService,
        private dialogRef: MatDialogRef<CreateVariationDialogComponent>,
        @Inject(MAT_DIALOG_DATA) private data: { item: any, existingVariations?: Variation[] }
    ) {
        this.existingVariations = data?.existingVariations || [];
    }

    ngOnInit() {
        this.setupAutocomplete();

        const itemClass = this.data.item.class || this.data.item.objectType;
        this.itemDataService.getVariationTemplates(itemClass).pipe(take(1)).subscribe({
            next: (response) => {
                this.variationTemplates = response;
                this.groupTemplatesByLocationScope();
                const selectedId = this.templateForm.controls['template'].value;
                const stillValid = this.variationTemplates.find(t => t.id === selectedId);
                if (!stillValid && this.variationTemplates.length > 0) {
                    this.templateForm.controls['template'].setValue(this.variationTemplates[0].id);
                }
                this.buildForm();
                this.loadLocationSets();
            },
            error: (error) => {
                this.notificationService.error(error?.error?.error?.message || API_ERRORS.GenericErrorMessage);
            }
        });
    }

    duplicateVariationLabelValidator(): ValidatorFn {
        return (control: AbstractControl): { [key: string]: any } | null => {
            if (!control.value) {
                return null;
            }
            const isDuplicate = this.existingVariations.some(
                v => v.variationLabel?.toLowerCase() === control.value?.toLowerCase()
            );
            return isDuplicate ? {duplicateVariationLabel: {value: control.value}} : null;
        };
    }

    groupTemplatesByLocationScope() {
        const scopeMap = new Map<string, any[]>();

        for (const template of this.variationTemplates) {
            if (!template.locationScopes || template.locationScopes.length === 0) {
                if (!scopeMap.has('Other')) {
                    scopeMap.set('Other', []);
                }
                scopeMap.get('Other').push(template);
            } else {
                for (const scope of template.locationScopes) {
                    if (!scopeMap.has(scope)) {
                        scopeMap.set(scope, []);
                    }
                    scopeMap.get(scope).push(template);
                }
            }
        }

        this.groupedTemplates = Array.from(scopeMap.entries())
            .map(([scope, templates]) => ({ scope: this.formatScopeLabel(scope), templates }))
            .sort((a, b) => {
                if (a.scope === 'Chain') return -1;
                if (b.scope === 'Chain') return 1;
                if (a.scope === 'Other') return 1;
                if (b.scope === 'Other') return -1;
                return a.scope.localeCompare(b.scope);
            });
    }

    formatScopeLabel(scope: string): string {
        if (!scope) return scope;
        return scope.charAt(0).toUpperCase() + scope.slice(1).toLowerCase();
    }

    atLeastOneRequiredFieldValidator(): ValidatorFn {
        return (formGroup: AbstractControl): { [key: string]: any } | null => {
            if (!this.templateAttributes || this.templateAttributes.length === 0) {
                return null;
            }

            const hasAtLeastOneFilledRequiredField = this.templateAttributes
                .filter(attr => attr.required)
                .some(attr => {
                    const control = formGroup.get(attr.jsonProperty);
                    if (!control) return false;

                    const value = control.value;

                    if (value === null || value === undefined || value === '') {
                        return false;
                    }

                    if (Array.isArray(value)) {
                        return value.length > 0;
                    }

                    return true;
                });

            return hasAtLeastOneFilledRequiredField ? null : { atLeastOneRequired: true };
        };
    }

    setupAutocomplete() {
        this.filteredLocationSets = this.templateForm.controls['location'].valueChanges.pipe(
            startWith(''),
            rxMap(value => this._filterLocationSets(value || ''))
        );
    }

    buildForm() {
        this.formReady = false;
        const currentTemplate = this.variationTemplates.find(template =>
            template.id === this.templateForm.controls['template'].value);
        if (!currentTemplate) {
            this.templateAttributes = [];
            this.form = new FormGroup({});
            this.formReady = true;
            return;
        }

        this.templateAttributes = (currentTemplate.attributeDetails || []).map(detail => {
            const attributeType = detail.attributeType;
            let uiType = attributeType === 'LIST' ? 'TEXT' : attributeType;
            const isDefaultTagNoOptions = (
                (detail.tag === 'DEFAULT_TAG' || detail.jsonProperty === 'defaultTag') &&
                (
                    !detail.options ||
                    (typeof detail.options === 'string' && detail.options.trim() === '')
                )
            );
            if (isDefaultTagNoOptions) {
                uiType = 'TEXT';
            }
            let options = detail.options;
            if (typeof options === 'string') {
                const rawList = options.split(',').map(o => o.trim()).filter(o => o.length);
                if (attributeType === 'BOOL') {
                    options = rawList.map(o => ({
                        value: o.toLowerCase() === 'true',
                        label: o.charAt(0).toUpperCase() + o.slice(1).toLowerCase()
                    }));
                } else {
                    // The regex below turns underscores into spaces & capitalizes the first letter of each word
                    options = rawList.map(o => ({
                        value: o,
                        label: o.replace(/_/g,' ').replace(/\b\w/g,(c) => c.toUpperCase())
                    }));
                }
            } else if (Array.isArray(options)) {
                options = options.map(o => typeof o === 'string' ? { value: o, label: o } : o);
            } else {
                if (attributeType === 'BOOL') {
                    options = [
                        { value: true, label: 'True' },
                        { value: false, label: 'False' }
                    ];
                } else if (attributeType === 'SELECT' || attributeType === 'MULTI_SELECT') {
                    options = isDefaultTagNoOptions ? null : [];
                } else {
                    options = null;
                }
            }
            return {
                jsonProperty: detail.jsonProperty,
                type: uiType,
                rawType: attributeType,
                name: formatFieldName(detail.name),
                description: detail.description,
                required: detail.required,
                options,
                defaultValue: detail.defaultValue
            };
        });

        this.templateAttributes.unshift({
            jsonProperty: 'variationLabel',
            type: 'TEXT',
            rawType: 'TEXT',
            name: 'Variation Label',
            description: 'Label that identifies where this variation will be used',
            required: true,
            options: null,
            defaultValue: ''
        });
        this.templateAttributes.unshift({
            jsonProperty: 'variationDescription',
            type: 'TEXT',
            rawType: 'TEXT',
            name: 'Variation Description',
            description: 'Description that identifies what this variation will be used for',
            required: true,
            options: null,
            defaultValue: ''
        });

        this.metadataAttributes = this.templateAttributes.filter(attr =>
            attr.jsonProperty === 'variationLabel' || attr.jsonProperty === 'variationDescription'
        );

        this.overrideAttributes = this.templateAttributes.filter(attr =>
            attr.jsonProperty !== 'variationLabel' && attr.jsonProperty !== 'variationDescription'
        ).sort((a, b) => {
            if (a.required === b.required) {
                return (a.name || '').localeCompare(b.name || '');
            }
            return a.required ? -1 : 1;
        });

        const formGroup: Record<string, FormControl> = {};
        for (const attribute of this.templateAttributes) {
            const validators = [];

            if (attribute.jsonProperty === 'variationLabel') {
                validators.push(this.duplicateVariationLabelValidator());
            }

            let initialValue = attribute.defaultValue ?? null;

            if (attribute.jsonProperty === 'disabled') {
                initialValue = true;
            }

            switch (attribute.rawType) {
                case 'NUM':
                    if (initialValue !== null && initialValue !== '') {
                        const num = Number(initialValue);
                        initialValue = isNaN(num) ? null : num;
                    }
                    break;
                case 'BOOL':
                    if (typeof initialValue === 'string') {
                        initialValue = initialValue.toLowerCase() === 'true';
                    } else if (typeof initialValue !== 'boolean') {
                        initialValue = false;
                    }
                    break;
                case 'LIST':
                    // Represent list as comma-separated string for editing as TEXT
                    if (Array.isArray(initialValue)) {
                        initialValue = initialValue.join(', ');
                    } else if (initialValue == null) {
                        initialValue = '';
                    }
                    break;
                case 'MULTI_SELECT':
                    if (!Array.isArray(initialValue)) initialValue = [];
                    break;
                default:
                    break;
            }

            if (attribute.jsonProperty === 'disabled') {
                formGroup[attribute.jsonProperty] = new FormControl({
                    value: true,
                    disabled: true
                }, validators);
            } else {
                formGroup[attribute.jsonProperty] = new FormControl({
                    value: initialValue,
                    disabled: false
                }, validators);
            }
        }

        this.form = new FormGroup(formGroup, this.atLeastOneRequiredFieldValidator());
        this.formReady = true;
    }

    close(): void {
        this.dialogRef.close();
    }

    save() {
        if (!this.selectedLocationSet) {
            this.notificationService.error('Please select a valid location set');
            return;
        }

        const overrides: Record<string, unknown> = {};
        for (const attribute of this.templateAttributes) {
            // Skip variationLabel and variationDescription - they should be at the top level, not in overrides
            if (attribute.jsonProperty === 'variationLabel' || attribute.jsonProperty === 'variationDescription') {
                continue;
            }

            const control = this.form.get(attribute.jsonProperty);
            if (!control) continue;
            let value = control.value;
            switch (attribute.rawType) {
                case 'BOOL':
                    break;
                case 'NUM':
                    if (value === '' || value === null) {
                        value = null;
                    } else {
                        const num = Number(value);
                        value = isNaN(num) ? null : num;
                    }
                    break;
                case 'LIST':
                    if (typeof value === 'string') {
                        value = value.split(/[,\n]/).map(v => v.trim()).filter(v => v.length);
                    } else if (!Array.isArray(value)) {
                        value = [];
                    }
                    break;
                case 'MULTI_SELECT':
                    if (!Array.isArray(value)) value = [];
                    break;
            }
            overrides[attribute.jsonProperty] = value;
        }

        const newVariation: CreateVariation = {
            base: false,
            itemTag: this.data.item.tag,
            locationId: this.selectedLocationSet.tag,
            overrides,
            variationLabel: this.form.get('variationLabel')?.value,
            variationDescription: this.form.get('variationDescription')?.value,
        };
        this.dialogRef.close(newVariation);
    }

    onDateChange(event: any) {
        this.effectiveDate = event?.value ?? null;
    }

    loadLocationSets() {
        const currentTemplate = this.variationTemplates.find(template =>
            template.id === this.templateForm.controls['template'].value);

        if (!currentTemplate || !currentTemplate.locationScopes || currentTemplate.locationScopes.length === 0) {
            this.locationSets = [];
            return;
        }

        // Load location sets for all location scopes in the template
        const locationSetObservables: Observable<LocationSet[]>[] = currentTemplate.locationScopes.map(scope =>
            this.locationSetService.getAllLocationSetsByTag(scope)
        );

        forkJoin(locationSetObservables).pipe(take(1)).subscribe({
            next: (responses: LocationSet[][]) => {
                const allLocationSets: LocationSet[] = responses.reduce((acc, curr) => acc.concat(curr), []);
                this.locationSets = Array.from(
                    new Map(allLocationSets.map(ls => [ls.tag, ls])).values()
                );
                this.setupAutocomplete();
            },
            error: (error) => {
                this.notificationService.error(error?.error?.error?.message || API_ERRORS.GenericErrorMessage);
                this.locationSets = [];
            }
        });
    }

    _filterLocationSets(value: string | LocationSet): LocationSet[] {
        if (!value) {
            return this.locationSets;
        }

        if (typeof value !== 'string') {
            return this.locationSets;
        }
        const filterValue = value.toLowerCase();
        return this.locationSets.filter(ls =>
            ls.name?.toLowerCase().includes(filterValue) ||
            ls.tag?.toLowerCase().includes(filterValue)
        );
    }

    displayLocationSet(locationSet: LocationSet): string {
        return locationSet?.name || '';
    }

    onLocationSelected(event: any) {
        this.selectedLocationSet = event.option.value;
    }

    onTemplateChange() {
        this.selectedLocationSet = null;
        this.templateForm.controls['location'].setValue('');
        this.selectedTabIndex = 0;
        this.buildForm();
        this.loadLocationSets();
    }
}
